import React, { useState } from 'react'
import { Button, HoverCard, Popover, Stack, Text, UnstyledButton } from '@mantine/core'
import moment from 'moment'
import { Hold, HoldLevel } from 'app/models/hold-params'
import { holdPanelTitle } from './holds/hold-title'

// One active hold to render in the card, plus the context needed to title it
// and (optionally) lift it.
export interface HoldIndicatorEntry {
  hold: Hold
  // The level this hold occupies in the current view. Kept for sorting and
  // legacy consumers — the accordion/panel title is now `title` (computed by
  // holdPanelTitle), not derived from this field.
  level: HoldLevel
  // Lift handler. The CALLER decides liftability by context: provide it only
  // when the hold was placed at the entity being viewed (a facility hold on the
  // facility page, a trade hold on the trade). A hold cascaded from above is
  // read-only here, so the caller omits it.
  onLift?: () => void
  // Source name to show as a "Cascaded From" field — set by the caller ONLY
  // when the hold cascaded from a level ABOVE the entity being viewed (e.g. a
  // deal name on the facility page, or a facility name on a trade). Omitted for
  // a hold placed at the current level (it isn't "cascaded from" anywhere).
  cascadedFrom?: string
  // Panel heading (e.g. "Trade Lock", "Agent Lock") and optional "Cascaded
  // from {source}" subtitle — computed by holdPanelTitle from the hold's
  // origin/system relative to the level being viewed.
  title: string
  subtitle?: string
}

interface Props {
  // Active holds to display, in level order (Trade → Facility → Deal).
  // Lifted/empty entries are ignored; if none are active the indicator renders
  // nothing.
  holds: HoldIndicatorEntry[]
  // Red title-bar text. Optional override; when omitted the header is derived
  // from the holds' systems (see deriveDetailsTitle) so every surface labels the
  // card consistently — "Agent Hold Details" / "Lock Details" / "Lock/Hold Details".
  title?: string
  // Accessible label for the icon.
  ariaLabel?: string
  // Help text shown under the sections. Omitted → no footer.
  footerText?: string
  // Badge glyph. 'octagon' (default) is the facility/deal pause badge;
  // 'padlock' is used on trade-related views. 'auto' derives a single
  // representative glyph from the systems present: padlock when any LendOS lock
  // exists, otherwise octagon for agent-only holds. A mixed entity shows the
  // padlock at a glance; the popover card's panels and "Lock/Hold Details" title
  // carry the full per-system breakdown.
  variant?: 'octagon' | 'padlock' | 'auto'
  // How the card opens. 'hover' (default) for read-only info cards; 'click'
  // (a Popover that stays open) when the card has actionable buttons (Lift).
  interaction?: 'hover' | 'click'
  // 'stacked' (default) shows every active hold at once; 'accordion' shows one
  // panel open at a time with informative collapsed headers for the rest —
  // used on trade views to keep multi-lock cards compact.
  layout?: 'stacked' | 'accordion'
  // Hover-mode only: makes the badge clickable (hover still shows the card,
  // click fires this) — used on the facility view where hovering shows the
  // read-only card and clicking the icon opens the Lift form. Ignored in
  // click/Popover mode, which uses in-card Lift buttons instead.
  onIconClick?: () => void
  // Trigger badge glyph size in px (default 20). Facility/deal views pass a
  // larger size so the octagon matches the adjacent alert icons (e.g. the
  // non-pro-rata icon, 24px).
  size?: number
}

// Figma palette for the hold badge / card.
// Exported so other alert glyphs in the same cell (e.g. the "not refreshed"
// triangle) stay the same red rather than drifting to a near-miss shade.
export const LOCK_RED = '#dc2626'
// Matches the Facilities table header row background.
// Exported with LOCK_RED and FieldBlock so sibling alert cards (e.g. the "not
// refreshed" triangle in the same Alert column) are built from these rather
// than a copy that drifts on the next tweak.
export const CARD_BODY_BG = '#F0EEEE'

// Fixed width for the trade (Popover) card so it doesn't jump horizontally as
// panels expand/collapse. Sized to comfortably fit the expanded fields.
export const TRADE_CARD_WIDTH = 320

const formatPlacedOn = (raw?: string | null) => {
  if (!raw) return '—'
  const m = moment(raw)
  return m.isValid() ? m.format('MMMM Do, YYYY [at] h:mm A') : '—'
}

const formatExpires = (raw?: string | null) => {
  if (!raw) return null
  const m = moment(raw)
  return m.isValid() ? m.format('MMMM Do, YYYY') : null
}

// Red octagon with a pause glyph — the facility/deal hold badge (lucide's
// OctagonPause). Inlined so we don't pull in another icon dependency.
function OctagonPauseIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10 15V9" />
      <path d="M14 15V9" />
      <path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z" />
    </svg>
  )
}

// Closed padlock — the trade-view hold badge (matches the prior trade lock
// icon). Inlined for consistency with the octagon badge.
function PadlockIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

function BadgeIcon({ variant, size }: { variant: 'octagon' | 'padlock'; size?: number }) {
  return variant === 'padlock' ? <PadlockIcon size={size} /> : <OctagonPauseIcon size={size} />
}

// The single glyph an 'auto' badge shows, derived from the systems present: a
// padlock when ANY LendOS lock exists (there is something within LendOS's
// control), otherwise an octagon when only SP Agent holds exist (out of LendOS's
// hands). The full per-system breakdown lives in the popover card.
function deriveVariant(entries: HoldIndicatorEntry[]): 'padlock' | 'octagon' {
  return entries.some(e => e.hold.system === 'LendOS') ? 'padlock' : 'octagon'
}

// Card title-bar text. A single-hold card carries no per-panel title, so its header
// names the origin and level directly ("Trade Lock Details", "Agent Trade Hold
// Details") — a reader is never left guessing which level or placer it is. Multi-hold
// cards fall back to a generic grouping — "Lock Details" when only LendOS locks are
// present, "Agent Hold Details" when only Signing Portal (agent) holds are,
// "Lock/Hold Details" when both — and name each level in its own panel.
export function deriveDetailsTitle(entries: HoldIndicatorEntry[]): string {
  const hasLendOS = entries.some(e => e.hold.system === 'LendOS')
  const hasSP = entries.some(e => e.hold.system === 'SigningPortal')
  if (entries.length === 1) {
    const { hold } = entries[0]
    return `${holdPanelTitle(hold, hold.origin.level).title} Details`
  }
  if (hasLendOS && hasSP) return 'Lock/Hold Details'
  return hasSP ? 'Agent Hold Details' : 'Lock Details'
}

// The badge glyph for a single hold: octagon for an SP hold, padlock otherwise.
const variantForHold = (h: Hold): 'octagon' | 'padlock' =>
  h.system === 'SigningPortal' ? 'octagon' : 'padlock'

// Resolves a possibly-'auto' variant to a concrete glyph for the given holds.
const resolveVariant = (
  variant: 'octagon' | 'padlock' | 'auto',
  entries: HoldIndicatorEntry[],
): 'octagon' | 'padlock' => (variant === 'auto' ? deriveVariant(entries) : variant)

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{
        transform: open ? 'rotate(90deg)' : 'none',
        transition: 'transform 120ms ease',
        flexShrink: 0,
      }}
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  )
}

export function FieldBlock({
  label,
  value,
  noWrap,
}: {
  label: string
  value: string
  noWrap?: boolean
}) {
  return (
    <div>
      <Text weight={700} color="#222" style={{ fontSize: 14, lineHeight: 1.4 }}>
        {label}:
      </Text>
      <Text
        color="#333"
        style={{ fontSize: 14, lineHeight: 1.4, whiteSpace: noWrap ? 'nowrap' : 'normal' }}
      >
        {value}
      </Text>
    </div>
  )
}

// Bordered outline button matching the card's red theme (mirrors the old
// popover Lift button, which read as a proper button with a border).
function LiftButton({ onLift }: { onLift: () => void }) {
  return (
    <Button
      variant="outline"
      color="red"
      radius="sm"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        onLift()
      }}
      styles={{
        root: {
          height: 'auto',
          padding: '3px 12px',
          borderColor: LOCK_RED,
          color: LOCK_RED,
        },
        label: { fontSize: 13, fontWeight: 600 },
      }}
    >
      Lift Lock
    </Button>
  )
}

// Panel title treatment: uppercase + tracking makes it read as a section
// header, distinct from the sentence-case field labels below it — without
// increasing the font size (it's actually smaller).
const PANEL_TITLE_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: '#1f2937',
  lineHeight: 1.4,
}

// The detail body of a single hold: the hold's fields, plus a "Cascaded From"
// field (under Notes) when the hold came from a higher level. Title/lift live
// in the caller (stacked section or accordion header). The accordion header
// already renders the cascade info as a subtitle, so it passes
// showCascadedFrom={false} to avoid showing it twice.
function HoldDetails({
  hold,
  cascadedFrom,
  showCascadedFrom = true,
}: {
  hold: Hold
  cascadedFrom?: string
  showCascadedFrom?: boolean
}) {
  const expires = formatExpires(hold.expirationDate)
  return (
    <Stack style={{ gap: 6 }}>
      <FieldBlock label="Reason" value={hold.reason || '—'} />
      <FieldBlock label="Placed By" value={hold.placedByName || '—'} noWrap />
      <FieldBlock label="Placed On" value={formatPlacedOn(hold.placedAt)} noWrap />
      {expires && <FieldBlock label="Expires" value={expires} />}
      <FieldBlock label="Notes" value={hold.notes || '—'} />
      {showCascadedFrom && cascadedFrom && <FieldBlock label="Cascaded From" value={cascadedFrom} />}
    </Stack>
  )
}

// The caller decides liftability by context (see onLift on HoldIndicatorEntry).
const canLiftEntry = (entry: HoldIndicatorEntry) => !!entry.onLift

// Stacked layout: every active hold shown at once. Used for single-hold cards
// and the facility/deal views.
function StackedBody({ entries }: { entries: HoldIndicatorEntry[] }) {
  return (
    <>
      {entries.map((entry, i) => (
        <div key={entry.hold.lockId}>
          {i > 0 && <div style={{ borderTop: '1px solid #d9d9d9', margin: '4px 0 8px' }} />}
          {/* No per-level title on single-lock cards — the red header already
              says "Locked" and the surface (e.g. the facility page) identifies
              the lock. */}
          <HoldDetails hold={entry.hold} cascadedFrom={entry.cascadedFrom} />
          {canLiftEntry(entry) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <LiftButton onLift={entry.onLift!} />
            </div>
          )}
        </div>
      ))}
    </>
  )
}

// Accordion layout: one panel open at a time. Collapsed rows still show the
// level and origin ("Facility Lock — Facility X") so nothing important is
// hidden. Used on trade views for compact multi-lock cards.
function AccordionBody({ entries }: { entries: HoldIndicatorEntry[] }) {
  // Default the first panel open; -1 = all collapsed. Clicking an open panel's
  // header collapses it (so it doesn't feel broken) — collapsing all is a valid
  // user-driven state.
  const [openIndex, setOpenIndex] = useState<number>(0)
  return (
    <div>
      {entries.map((entry, i) => {
        const open = i === openIndex
        return (
          <div
            key={entry.hold.lockId}
            style={i > 0 ? { borderTop: '1px solid #d9d9d9' } : undefined}
          >
            {/* Header row reads as a bar (subtle tint) to separate it from the
                fields below. Toggle and Lift are separate buttons so expanding
                never triggers a lift. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 10px',
                borderRadius: 4,
                backgroundColor: open ? '#E4E0E0' : 'transparent',
              }}
            >
              <UnstyledButton
                onClick={() => setOpenIndex(open ? -1 : i)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                }}
              >
                <span style={{ color: LOCK_RED, display: 'inline-flex' }}>
                  <Chevron open={open} />
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <Text
                    style={{
                      ...PANEL_TITLE_STYLE,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entry.title}
                  </Text>
                  {entry.subtitle && (
                    <Text
                      style={{
                        fontSize: 11,
                        color: '#6b7280',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.subtitle}
                    </Text>
                  )}
                </div>
                {/* System glyph at the right edge of the clickable header, forming
                    a scannable column of icons down a multi-lock card. */}
                <span style={{ color: LOCK_RED, display: 'inline-flex', flexShrink: 0 }}>
                  <BadgeIcon variant={variantForHold(entry.hold)} size={16} />
                </span>
              </UnstyledButton>
              {open && canLiftEntry(entry) && <LiftButton onLift={entry.onLift!} />}
            </div>
            {/* grid-template-rows 0fr→1fr animates open/close to/from auto height
                without a fixed pixel height; the locked side lets the card grow
                smoothly from its anchored edge. */}
            <div
              style={{
                display: 'grid',
                gridTemplateRows: open ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.3s ease',
              }}
            >
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                <div style={{ padding: '10px 0' }}>
                  <HoldDetails hold={entry.hold} cascadedFrom={entry.cascadedFrom} showCascadedFrom={false} />
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const LEVEL_RANK: Record<HoldLevel, number> = { Trade: 0, Facility: 1, Deal: 2 }

// A hold is liftable from the current view only when it's a native LendOS
// hold placed AT the level being viewed. A hold cascaded down from a level
// above (e.g. a deal hold shown on a facility page) or one owned by another
// system (SigningPortal) is read-only here.
export const isLiftable = (h: Hold, viewLevel: HoldLevel): boolean =>
  h.system === 'LendOS' && h.origin.level === viewLevel && !h.liftedAt

// Maps the flat `holds: Hold[]` list to indicator entries for a given view
// level: drops lifted holds, orders Trade → Facility → Deal, and marks each
// entry's `cascadedFrom` when its origin differs from the level being viewed.
// The caller supplies `makeLift` to decide per-hold liftability (typically
// wrapping `isLiftable`); it returns `undefined` for read-only holds.
export function holdsToEntries(
  holds: Hold[],
  viewLevel: HoldLevel,
  makeLift: (hold: Hold) => (() => void) | undefined,
): HoldIndicatorEntry[] {
  return holds
    .filter(h => !h.liftedAt)
    .slice()
    .sort((a, b) => LEVEL_RANK[a.origin.level] - LEVEL_RANK[b.origin.level])
    .map(h => ({
      hold: h,
      level: h.origin.level,
      cascadedFrom: h.origin.level !== viewLevel ? h.origin.sourceName : undefined,
      onLift: makeLift(h),
      ...holdPanelTitle(h, viewLevel),
    }))
}

// Read-only hold indicator: a red badge with a details card listing every active
// hold (trade / facility / deal), each with its origin and — for directly-placed
// holds — a Lift action. Renders nothing when no hold is active. Used across
// facility, deal, trade, and document surfaces.
export default function HoldIndicator({
  holds,
  title,
  ariaLabel,
  footerText,
  variant = 'octagon',
  interaction = 'hover',
  layout = 'stacked',
  onIconClick,
  size = 20,
}: Props) {
  const [opened, setOpened] = useState(false)
  // Side chosen once when the card opens (based on where the badge sits in the
  // viewport), then locked via flip:false so the card keeps its natural height
  // and never re-flips ("jumps") as panels expand/collapse.
  const [side, setSide] = useState<'top' | 'bottom'>('top')
  // Active holds; wrap each lift action so triggering it also closes this card
  // (otherwise the popover stays open behind the lift modal).
  const active = holds
    .filter(e => e.hold && !e.hold.liftedAt)
    .map(e =>
      e.onLift
        ? {
            ...e,
            onLift: () => {
              setOpened(false)
              e.onLift?.()
            },
          }
        : e,
    )

  if (active.length === 0) return null

  // Accordion only earns its chrome at 2+ locks; a single lock renders plainly.
  const useAccordion = layout === 'accordion' && active.length > 1

  // Open below when the badge is in the top half of the viewport, above when in
  // the bottom half — so the card grows into the roomier side.
  const openAt = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect()
    setSide(rect.top < window.innerHeight / 2 ? 'bottom' : 'top')
    setOpened(true)
  }

  // Derive the header from the holds so every surface labels the card the same
  // way ("Agent Hold Details" / "Lock Details" / "Lock/Hold Details"); a caller
  // may still override with an explicit `title`. This is independent of the
  // badge `variant`, which only controls the glyph.
  const effectiveTitle = title ?? deriveDetailsTitle(active)

  // Default the accessible label to the derived header + what activation does,
  // so a caller that omits ariaLabel announces the right state and action:
  // click mode opens the card on click; hover mode opens it on hover, and — in
  // hover mode only — when onIconClick is set, clicking the icon opens the Lift
  // form instead. An explicit ariaLabel still overrides.
  const defaultAriaLabel =
    interaction === 'click'
      ? `${effectiveTitle} — click for details`
      : onIconClick
        ? `${effectiveTitle} — hover for details; click to open the Lift form`
        : `${effectiveTitle} — hover for details`

  const dropdownStyles = {
    dropdown: {
      padding: 0,
      borderRadius: 8,
      backgroundColor: CARD_BODY_BG,
      minWidth: 240,
      maxWidth: 380,
    },
  }

  const cardBody = (
    <div style={{ borderRadius: 8, overflow: 'hidden' }}>
      <div
        style={{
          backgroundColor: LOCK_RED,
          color: '#fff',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {/* Multi-lock 'auto' cards let the "Lock/Hold Details" text + per-panel
            icons carry the systems, so the title bar is text-only. A single-lock
            card has no panel header, so it keeps its (unmixable) glyph here.
            Non-'auto' surfaces always show their glyph. */}
        {(variant !== 'auto' || active.length === 1) && (
          <BadgeIcon variant={resolveVariant(variant, active)} size={18} />
        )}
        <Text weight={700} style={{ color: '#fff', fontSize: 16, letterSpacing: '0.5px' }}>
          {effectiveTitle}
        </Text>
      </div>
      <Stack style={{ backgroundColor: CARD_BODY_BG, padding: '12px 16px', gap: 8 }}>
        {useAccordion ? <AccordionBody entries={active} /> : <StackedBody entries={active} />}
        {footerText && (
          <Text color="#888" style={{ fontSize: 12, lineHeight: 1.4 }}>
            {footerText}
          </Text>
        )}
      </Stack>
    </div>
  )

  const iconStyle: React.CSSProperties = {
    color: LOCK_RED,
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    cursor: interaction === 'click' || onIconClick ? 'pointer' : 'default',
  }

  // Click-to-open Popover: stays open so in-card actions (Lift) are clickable.
  if (interaction === 'click') {
    return (
      <Popover
        opened={opened}
        onChange={setOpened}
        position={side}
        // flip:false locks the side we picked at open, so the card doesn't jump
        // above↔below when panels expand/collapse. shift keeps it in view.
        middlewares={{ flip: false, shift: true }}
        withArrow
        arrowSize={12}
        shadow="md"
        withinPortal
        closeOnClickOutside
        styles={{
          dropdown: {
            ...dropdownStyles.dropdown,
            // Fixed width so the card doesn't shrink horizontally when panels
            // collapse (expanded panels are ~this wide anyway).
            minWidth: TRADE_CARD_WIDTH,
            maxWidth: TRADE_CARD_WIDTH,
            width: TRADE_CARD_WIDTH,
          },
          // Arrow matches the card edge it touches: red title bar when the card
          // is below the badge (arrow on top), gray body when above (arrow on
          // bottom).
          arrow: {
            backgroundColor: side === 'bottom' ? LOCK_RED : CARD_BODY_BG,
            border: 0,
          },
        }}
      >
        <Popover.Target>
          <span
            role="button"
            aria-label={ariaLabel ?? defaultAriaLabel}
            tabIndex={0}
            onClick={e => {
              e.stopPropagation()
              if (opened) setOpened(false)
              else openAt(e.currentTarget)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                if (opened) setOpened(false)
                else openAt(e.currentTarget)
              }
            }}
            style={iconStyle}
          >
            <BadgeIcon variant={resolveVariant(variant, active)} size={size} />
          </span>
        </Popover.Target>
        <Popover.Dropdown>{cardBody}</Popover.Dropdown>
      </Popover>
    )
  }

  // Hover card: read-only info (facility/deal views).
  return (
    <HoverCard
      position="top"
      shadow="md"
      withArrow
      arrowSize={12}
      openDelay={100}
      closeDelay={100}
      withinPortal
      styles={{
        dropdown: {
          ...dropdownStyles.dropdown,
          '&[data-position^="bottom"] .mantine-HoverCard-arrow': {
            backgroundColor: LOCK_RED,
            borderColor: LOCK_RED,
          },
        },
      }}
    >
      <HoverCard.Target>
        <span
          role={onIconClick ? 'button' : 'img'}
          aria-label={ariaLabel ?? defaultAriaLabel}
          tabIndex={0}
          // Always stop the click from bubbling so clicking the badge never
          // triggers a clickable table row's navigation (e.g. Related
          // Facilities); fire the lift action when one is provided.
          onClick={e => {
            e.stopPropagation()
            onIconClick?.()
          }}
          onKeyDown={
            onIconClick
              ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onIconClick()
                  }
                }
              : undefined
          }
          style={iconStyle}
        >
          <BadgeIcon variant={resolveVariant(variant, active)} size={size} />
        </span>
      </HoverCard.Target>
      <HoverCard.Dropdown>{cardBody}</HoverCard.Dropdown>
    </HoverCard>
  )
}
